/**
 * Modal for configuring site storage (Local, S3, or Supabase).
 */

import { useState } from 'react';
import {
  useStorageConnections,
  useCreateStorageConnection,
  useTestInlineStorageConnection,
  useUpdateSiteStorage,
  usePlatformConnections,
  connectionLabel,
  S3_PROVIDER_NAMES,
  type CreateStorageConnectionRequest,
  type StorageType,
  type S3Provider,
} from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface StorageConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteSlug: string;
}

export function StorageConfigModal({
  open,
  onOpenChange,
  siteSlug,
}: StorageConfigModalProps) {
  const { data: connectionsData } = useStorageConnections();
  const { data: platformConnectionsData } = usePlatformConnections(siteSlug);
  const createConnection = useCreateStorageConnection();
  const testInline = useTestInlineStorageConnection();
  const updateSiteStorage = useUpdateSiteStorage();

  // Get Cloudflare platform connections with account_id
  const cloudflareConnections = (platformConnectionsData?.data ?? []).filter(
    (c) => c.platform === 'cloudflare_pages' && c.account_id
  );

  // Form state
  const [mode, setMode] = useState<'select' | 'create'>('create');
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [connectionTested, setConnectionTested] = useState(false);
  const [name, setName] = useState('');
  const [storageType, setStorageType] = useState<StorageType>('local');

  // S3 fields
  const [s3Provider, setS3Provider] = useState<S3Provider>('aws');
  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountIdSource, setAccountIdSource] = useState<'connection' | 'manual'>('manual');
  const [publicUrlBase, setPublicUrlBase] = useState('');

  // Supabase fields
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [anonKey, setAnonKey] = useState('');
  const [supabaseBucket, setSupabaseBucket] = useState('media');

  // Helper to update field and reset test status
  const updateField = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => (value: T) => {
    setter(value);
    setConnectionTested(false);
  };

  const resetForm = () => {
    setMode('create');
    setSelectedConnectionId('');
    setConnectionTested(false);
    setName('');
    setStorageType('local');
    setS3Provider('aws');
    setBucket('');
    setRegion('us-east-1');
    setAccessKeyId('');
    setSecretAccessKey('');
    setEndpoint('');
    setAccountId('');
    setAccountIdSource('manual');
    setPublicUrlBase('');
    setSupabaseUrl('');
    setAnonKey('');
    setSupabaseBucket('media');
  };

  const buildTestData = (): CreateStorageConnectionRequest => {
    const data: CreateStorageConnectionRequest = {
      name: name.trim() || 'test',
      storage_type: storageType,
    };

    if (storageType === 's3') {
      data.bucket = bucket;
      data.provider = s3Provider;
      data.access_key_id = accessKeyId;
      data.secret_access_key = secretAccessKey;

      // Include region for providers that need it
      if (s3Provider !== 'cloudflare_r2') {
        data.region = region;
      }

      // Include account_id for R2
      if (s3Provider === 'cloudflare_r2' && accountId.trim()) {
        data.account_id = accountId;
      }

      // Include public_url_base for R2
      if (s3Provider === 'cloudflare_r2' && publicUrlBase.trim()) {
        data.public_url_base = publicUrlBase;
      }

      // Include endpoint for custom/minio
      if ((s3Provider === 'custom' || s3Provider === 'minio') && endpoint.trim()) {
        data.endpoint = endpoint;
      }
    } else if (storageType === 'supabase') {
      data.supabase_url = supabaseUrl;
      data.anon_key = anonKey;
      data.bucket = supabaseBucket;
    }

    return data;
  };

  const handleTestInline = async () => {
    const data = buildTestData();

    try {
      const result = await testInline.mutateAsync(data);
      if (result.data?.healthy) {
        toast.success('Connection successful!');
        setConnectionTested(true);
      } else {
        toast.error(result.data?.message || 'Connection test failed');
        setConnectionTested(false);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test failed');
      setConnectionTested(false);
    }
  };

  const handleSave = async () => {
    if (mode === 'select' && selectedConnectionId) {
      // Use existing connection
      try {
        await updateSiteStorage.mutateAsync({
          siteSlug,
          storageConnectionId: parseInt(selectedConnectionId, 10),
        });
        toast.success('Storage connection updated');
        onOpenChange(false);
        resetForm();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update storage');
      }
    } else {
      // Create new connection and assign it
      if (!name.trim()) {
        toast.error('Please enter a name for this connection');
        return;
      }

      const data = buildTestData();
      data.name = name.trim();

      try {
        const result = await createConnection.mutateAsync(data);
        await updateSiteStorage.mutateAsync({
          siteSlug,
          storageConnectionId: result.data!.id,
        });
        toast.success(`Storage connection "${name}" created and assigned`);
        onOpenChange(false);
        resetForm();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create connection');
      }
    }
  };

  const existingConnections = connectionsData?.data ?? [];
  const isPending = createConnection.isPending || updateSiteStorage.isPending;

  // Can save if: selecting existing, or (creating new with tested connection)
  const canSave =
    (mode === 'select' && selectedConnectionId) ||
    (mode === 'create' && connectionTested);

  return (
    <Dialog open={open} onOpenChange={(o) => !isPending && onOpenChange(o)} modal={false}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-auto"
      >
        <DialogHeader>
          <DialogTitle>Configure Storage</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode selector - only show if there are existing connections */}
          {existingConnections.length > 0 && (
            <div className="space-y-2">
              <Label>Mode</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === 'create'}
                    onChange={() => {
                      setMode('create');
                      setSelectedConnectionId('');
                    }}
                    className="rounded-full"
                  />
                  Create New
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === 'select'}
                    onChange={() => {
                      setMode('select');
                      setConnectionTested(false);
                    }}
                    className="rounded-full"
                  />
                  Use Existing
                </label>
              </div>
            </div>
          )}

          {mode === 'select' ? (
            <div className="space-y-2">
              <Label>Select Connection</Label>
              <Select
                value={selectedConnectionId}
                onValueChange={(v: string) => v && setSelectedConnectionId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a connection...">
                    {selectedConnectionId && (() => {
                      const conn = existingConnections.find(c => String(c.id) === selectedConnectionId);
                      return conn ? `${conn.name} (${conn.storage_type})` : selectedConnectionId;
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {existingConnections.map((conn) => (
                    <SelectItem key={conn.id} value={String(conn.id)}>
                      {conn.name} ({conn.storage_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedConnectionId && (
                <p className="text-sm text-muted-foreground">
                  {existingConnections.find(
                    (c) => String(c.id) === selectedConnectionId
                  )?.config_preview}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Connection Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Production S3"
                />
              </div>

              <div className="space-y-2">
                <Label>Storage Type</Label>
                <Select
                  value={storageType}
                  onValueChange={(v: string) => v && updateField(setStorageType)(v as StorageType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local (Development)</SelectItem>
                    <SelectItem value="s3">Amazon S3 / S3-Compatible</SelectItem>
                    <SelectItem value="supabase">Supabase Storage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {storageType === 'local' && (
                <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground">
                  Local storage uses the server filesystem. Best for development only.
                </div>
              )}

              {storageType === 's3' && (
                <>
                  <div className="space-y-2">
                    <Label>Provider</Label>
                    <Select
                      value={s3Provider}
                      onValueChange={(v: string) => v && updateField(setS3Provider)(v as S3Provider)}
                    >
                      <SelectTrigger>
                        <SelectValue>
                          {S3_PROVIDER_NAMES[s3Provider]}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.entries(S3_PROVIDER_NAMES) as [S3Provider, string][]).map(([key, name]) => (
                          <SelectItem key={key} value={key}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Account ID - only for R2 */}
                  {s3Provider === 'cloudflare_r2' && (
                    <div className="space-y-2">
                      <Label>Account ID</Label>
                      {cloudflareConnections.length > 0 ? (
                        <>
                          <Select
                            value={accountIdSource === 'connection' ? accountId : 'manual'}
                            onValueChange={(v: string) => {
                              if (!v) return;
                              if (v === 'manual') {
                                setAccountIdSource('manual');
                                setAccountId('');
                                setConnectionTested(false);
                              } else {
                                setAccountIdSource('connection');
                                setAccountId(v);
                                setConnectionTested(false);
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select account...">
                                {accountIdSource === 'connection' && accountId
                                  ? (() => {
                                      const conn = cloudflareConnections.find(c => c.account_id === accountId);
                                      return conn ? `${connectionLabel(conn)} (${accountId.slice(0, 8)}...)` : accountId;
                                    })()
                                  : accountIdSource === 'manual' && accountId
                                  ? accountId
                                  : 'Select account...'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {cloudflareConnections.map((conn) => (
                                <SelectItem key={conn.id} value={conn.account_id!}>
                                  {connectionLabel(conn)} ({conn.account_id!.slice(0, 8)}...)
                                </SelectItem>
                              ))}
                              <SelectItem value="manual">Enter manually</SelectItem>
                            </SelectContent>
                          </Select>
                          {accountIdSource === 'manual' && (
                            <Input
                              type="text"
                              value={accountId}
                              onChange={(e) => updateField(setAccountId)(e.target.value)}
                              placeholder="e.g., a1b2c3d4e5f6..."
                              className="mt-2"
                            />
                          )}
                          <p className="text-xs text-muted-foreground">
                            {accountIdSource === 'connection'
                              ? 'Using account ID from your Cloudflare Pages connection'
                              : 'Found in your Cloudflare dashboard URL or R2 settings'}
                          </p>
                        </>
                      ) : (
                        <>
                          <Input
                            type="text"
                            value={accountId}
                            onChange={(e) => updateField(setAccountId)(e.target.value)}
                            placeholder="e.g., a1b2c3d4e5f6..."
                          />
                          <p className="text-xs text-muted-foreground">
                            Found in your Cloudflare dashboard URL or R2 settings
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Bucket Name</Label>
                    <Input
                      type="text"
                      value={bucket}
                      onChange={(e) => updateField(setBucket)(e.target.value)}
                      placeholder="my-media-bucket"
                    />
                  </div>

                  {/* Region - not needed for R2 */}
                  {s3Provider !== 'cloudflare_r2' && (
                    <div className="space-y-2">
                      <Label>Region</Label>
                      <Select value={region} onValueChange={(v: string) => v && updateField(setRegion)(v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                          <SelectItem value="us-west-1">US West (N. California)</SelectItem>
                          <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                          <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                          <SelectItem value="eu-central-1">EU (Frankfurt)</SelectItem>
                          <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                          <SelectItem value="ap-northeast-1">Asia Pacific (Tokyo)</SelectItem>
                          {/* DigitalOcean regions */}
                          <SelectItem value="nyc3">NYC3 (DigitalOcean)</SelectItem>
                          <SelectItem value="sfo3">SFO3 (DigitalOcean)</SelectItem>
                          <SelectItem value="ams3">AMS3 (DigitalOcean)</SelectItem>
                          <SelectItem value="sgp1">SGP1 (DigitalOcean)</SelectItem>
                          <SelectItem value="fra1">FRA1 (DigitalOcean)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Access Key ID</Label>
                    <Input
                      type="text"
                      value={accessKeyId}
                      onChange={(e) => updateField(setAccessKeyId)(e.target.value)}
                      placeholder={s3Provider === 'cloudflare_r2' ? 'R2 Access Key ID' : 'AKIAIOSFODNN7EXAMPLE'}
                    />
                    {s3Provider === 'cloudflare_r2' && (
                      <p className="text-xs text-muted-foreground">
                        Create R2 API tokens in Cloudflare Dashboard → R2 → Manage R2 API Tokens
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Secret Access Key</Label>
                    <Input
                      type="password"
                      value={secretAccessKey}
                      onChange={(e) => updateField(setSecretAccessKey)(e.target.value)}
                    />
                  </div>

                  {/* Public URL - only for R2 */}
                  {s3Provider === 'cloudflare_r2' && (
                    <div className="space-y-2">
                      <Label>Public URL (R2.dev or Custom Domain)</Label>
                      <Input
                        type="text"
                        value={publicUrlBase}
                        onChange={(e) => updateField(setPublicUrlBase)(e.target.value)}
                        placeholder="https://pub-abc123.r2.dev"
                      />
                      <p className="text-xs text-muted-foreground">
                        Enable public access in R2 bucket settings → R2.dev subdomain, or connect a custom domain
                      </p>
                    </div>
                  )}

                  {/* Custom endpoint - only for custom/minio */}
                  {(s3Provider === 'custom' || s3Provider === 'minio') && (
                    <div className="space-y-2">
                      <Label>Endpoint URL</Label>
                      <Input
                        type="text"
                        value={endpoint}
                        onChange={(e) => updateField(setEndpoint)(e.target.value)}
                        placeholder="https://s3.example.com"
                      />
                      <p className="text-xs text-muted-foreground">
                        The S3-compatible API endpoint URL
                      </p>
                    </div>
                  )}
                </>
              )}

              {storageType === 'supabase' && (
                <>
                  <div className="space-y-2">
                    <Label>Supabase Project URL</Label>
                    <Input
                      type="text"
                      value={supabaseUrl}
                      onChange={(e) => updateField(setSupabaseUrl)(e.target.value)}
                      placeholder="https://abcdefgh.supabase.co"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Service Role Key</Label>
                    <Input
                      type="password"
                      value={anonKey}
                      onChange={(e) => updateField(setAnonKey)(e.target.value)}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Found in Project Settings → API → service_role key (has write access)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Bucket Name</Label>
                    <Input
                      type="text"
                      value={supabaseBucket}
                      onChange={(e) => updateField(setSupabaseBucket)(e.target.value)}
                      placeholder="media"
                    />
                    <p className="text-xs text-muted-foreground">
                      The storage bucket must already exist in your Supabase project
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          <div className="flex gap-2 pt-4">
            {mode === 'create' && (
              <Button
                variant="secondary"
                onClick={handleTestInline}
                disabled={testInline.isPending || isPending}
              >
                {testInline.isPending ? 'Testing...' : 'Test Connection'}
              </Button>
            )}
            <Button onClick={handleSave} disabled={!canSave || isPending}>
              {isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
